import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
    IApiResponse,
    IPaginatedApiResponse,
} from "../../../utils/types/api-response";
import {
    base64ToBlob,
    handleRemoveEmptyParamFromQueryParams,
} from "../../../utils/helpers";
import {
    IClientForm,
    IClientFormFilters,
    IConsentFormFilters,
    IConsentFormRecord,
    IConsentFormsResponse,
    IFormDocument,
} from "../types/client-forms.types";
import {
    fetchFormDocument,
    getClientConsents,
    getClientForms,
    getConsentByBookingId,
} from "../services/client-forms.services";

export function useGetClientForms(filters: IClientFormFilters) {
    return useQuery<
        IPaginatedApiResponse<IClientForm[]>,
        AxiosError<IPaginatedApiResponse<IClientForm[]>>
    >({
        queryKey: [
            "client-forms",
            handleRemoveEmptyParamFromQueryParams(filters),
        ],
        queryFn: () => getClientForms(filters),
    });
}

export function useGetClientConsents(filters?: IConsentFormFilters) {
    return useQuery<
        IConsentFormsResponse,
        AxiosError<IPaginatedApiResponse<IConsentFormRecord[]>>
    >({
        queryKey: [
            "client-consents",
            handleRemoveEmptyParamFromQueryParams(filters || {}),
        ],
        queryFn: () => getClientConsents(filters),
    });
}

export function useGetConsentByBookingId(bookingId: string, enabled = true) {
    return useQuery<
        IApiResponse<IConsentFormRecord>,
        AxiosError<IApiResponse<IConsentFormRecord>>
    >({
        queryKey: ["consent", bookingId],
        queryFn: () => getConsentByBookingId(bookingId),
        enabled: Boolean(bookingId) && enabled,
    });
}

export function useFetchFormDocument(fileUrl: string, enabled: boolean) {
    return useQuery<
        { data: string; content_type?: string },
        AxiosError<{ data: string }>
    >({
        queryKey: ["form-document", fileUrl],
        queryFn: () => fetchFormDocument(fileUrl),
        enabled,
    });
}

export async function downloadFormDocumentFile(
    document: IFormDocument
): Promise<void> {
    const result = await fetchFormDocument(document.document_url);
    const contentType = result.content_type ?? document.content_type;
    const file = base64ToBlob(result.data, contentType);
    const href = URL.createObjectURL(file);
    const anchor = window.document.createElement("a");
    anchor.href = href;
    anchor.download = (document.document_name || "consent-document")
        .split(/[\\/]/)
        .pop()!
        .replace(/[<>:"|?*]/g, "_");
    window.document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(href), 1000);
}
